/**
 * 反向推送（PC → 设备）的核心分发逻辑：根据文本特征自动选择 INJECT_TEXT
 * 还是 SET_CLIPBOARD paste=true。抽出来是为了让 FloatingMirror 的工具栏
 * 按钮、抽屉里的"→ 推送到手机"行内动作、键盘快捷键、底部"粘贴主机剪
 * 贴板"快捷动作 这四个入口共用同一份语义；不要再各自拷一份。
 *
 * 决策来自 scrcpy 协议（v3.3.4 / commit fb6381f）：
 *
 *   - INJECT_TEXT 走 Android 的 KeyCharacterMap，**只能编码 ASCII**——
 *     CJK / emoji / 重音字符 / 西里尔字母都会被设备端静默丢弃。这是
 *     "中文粘不上"的根因。
 *   - INJECT_TEXT 受协议层 hard-cap 300 UTF-8 字节限制；超长 ASCII
 *     一样要 pivot。
 *   - SET_CLIPBOARD paste=true 没有这两条限制：服务器把文字写到设备
 *     剪贴板再注入一次粘贴，从用户视角看效果完全一致，但能扛住任意
 *     长度 + 任意字符。
 *
 * 所以策略是：**短 ASCII 优先 INJECT_TEXT（更直接、不污染设备剪贴板）；
 * 任何不满足这两条的 → 全部 SET_CLIPBOARD paste**。
 */
import {
  packInjectText,
  packSetClipboard,
} from '@/lib/scrcpy/control';

/**
 * scrcpy 协议层 INJECT_TEXT 的硬上限。和服务端 ControlMessageReader 对齐
 * 即可，不要再调小（设备端做了同样的判断；过短反而走不必要的剪贴板路径）。
 */
export const INJECT_TEXT_BYTE_LIMIT = 300;

/** Returns true iff every codepoint in `s` lives in [0, 0x7f]. */
export function isAsciiOnly(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    if (s.charCodeAt(i) > 0x7f) return false;
  }
  return true;
}

const utf8Enc = new TextEncoder();
export function utf8Bytes(s: string): number {
  return utf8Enc.encode(s).length;
}

/**
 * Pure picker — choose the wire-encoding for `text`. Exposed for the
 * unit tests; production code should call {@link pushTextToDevice}.
 */
export function chooseWireMode(text: string): 'inject' | 'clipboard-paste' {
  if (isAsciiOnly(text) && utf8Bytes(text) <= INJECT_TEXT_BYTE_LIMIT) {
    return 'inject';
  }
  return 'clipboard-paste';
}

export interface PushTextToDeviceResult {
  /** Which protocol path we ended up using. */
  mode: 'inject' | 'clipboard-paste' | 'clipboard-set' | 'noop';
  /** UTF-8 byte length (0 when noop). */
  bytes: number;
  /** Character count (0 when noop). */
  chars: number;
}

export interface PushTextToDeviceOptions {
  /**
   * Whether to "paste" after writing the device clipboard.
   *
   *   - `true` (default): full paste semantics — by default short ASCII
   *     goes through INJECT_TEXT (direct keystroke replay), the rest
   *     writes SET_CLIPBOARD with paste=true (server forces a paste
   *     action into the focused EditText). This is the right behaviour
   *     for user-driven "paste my host clipboard onto the device"
   *     actions (Cmd+V shortcut, drag-drop). When the caller also wants
   *     the device clipboard to mutate, see {@link forceClipboard}.
   *   - `false`: pure "copy" semantics — only writes the device's
   *     system clipboard via SET_CLIPBOARD with paste=false; nothing
   *     is auto-pasted. INJECT_TEXT is intentionally skipped in this
   *     mode because INJECT_TEXT is keystroke injection, not clipboard
   *     mutation.
   */
  paste?: boolean;
  /**
   * When `paste === true`, force the SET_CLIPBOARD path even for short
   * ASCII (which would otherwise go through INJECT_TEXT). Use this for
   * any UX where the user expects the **device clipboard** to actually
   * mutate as part of the action — e.g. the clipboard panel's "复制到
   * 手机" main action and the Composer's "保存并复制到手机" CTA. The
   * literal label "复制" promises the device clipboard reflects the
   * text, and INJECT_TEXT only injects keystrokes; without this flag
   * users see the text appear in the EditText but the next long-press
   * → 粘贴 still serves whatever was in the clipboard before.
   *
   * Ignored when `paste === false` (paste=false already always uses
   * SET_CLIPBOARD).
   */
  forceClipboard?: boolean;
}

/**
 * Send `text` to the device through the supplied scrcpy control sender.
 *
 * `send` should accept a single Uint8Array (the same callable used by
 * `MirrorCanvasHandle.sendControl`); we intentionally don't depend on
 * the canvas type here so this helper is trivially testable with a mock.
 *
 * Empty / null / undefined input is a no-op (mirrors the user clicking
 * "paste" with an empty system clipboard — silently drop, the caller
 * surfaces the toast).
 */
export function pushTextToDevice(
  send: (bytes: Uint8Array) => void,
  text: string | null | undefined,
  opts?: PushTextToDeviceOptions,
): PushTextToDeviceResult {
  if (!text) return { mode: 'noop', bytes: 0, chars: 0 };
  const paste = opts?.paste ?? true;
  const forceClipboard = opts?.forceClipboard ?? false;
  // SET_CLIPBOARD's `sequence` field exists so the server can later ack
  // via the device-message channel; we don't currently consume the ack
  // (the toast is local), but the value still has to be monotonic per
  // scrcpy convention. Date.now() is unique enough at human speeds.
  const sequence = BigInt(Date.now());
  if (!paste) {
    // Pure clipboard write — never use INJECT_TEXT (which is input,
    // not clipboard). Always SET_CLIPBOARD paste=false.
    send(packSetClipboard({ sequence, paste: false, text }));
    return { mode: 'clipboard-set', bytes: utf8Bytes(text), chars: text.length };
  }
  // forceClipboard short-circuits the inject pivot: the caller wants
  // the device clipboard to actually update *and* the text to land in
  // the focused EditText. Without this branch, short ASCII would go
  // through INJECT_TEXT (keystroke replay only) and the user would see
  // the text appear but the device clipboard would still hold whatever
  // was there before — surprising for any UX whose label says "复制".
  if (forceClipboard) {
    send(packSetClipboard({ sequence, paste: true, text }));
    return { mode: 'clipboard-paste', bytes: utf8Bytes(text), chars: text.length };
  }
  const mode = chooseWireMode(text);
  if (mode === 'inject') {
    send(packInjectText(text));
    return { mode, bytes: utf8Bytes(text), chars: text.length };
  }
  send(packSetClipboard({ sequence, paste: true, text }));
  return { mode, bytes: utf8Bytes(text), chars: text.length };
}
